import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, PhoneCall, Clock, MessageSquare, CheckCircle2, X, CalendarClock, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBill } from "@/lib/format";
import { toast } from "sonner";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// localStorage key per user for auto-popup gate
const STORAGE_KEY = (uid) => `pending_popup_last_shown_${uid}`;
const CYCLE_DAYS = 15;

function daysSinceISO(iso) {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return Infinity;
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
}

export default function PendingRemindersDialog({ open, onOpenChange, autoTriggered, minDays = 15 }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [followUpRow, setFollowUpRow] = useState(null);
  const [waRow, setWaRow] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/pending-reminders", { params: { min_days: minDays } });
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [minDays]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const doAction = async (id, action, extra = {}) => {
    if (action === "clear") {
      const row = items.find((x) => x.id === id);
      const label = row ? `${row.customer_name} — ${formatBill(row.application_id || row.doc_no)}` : id;
      if (!window.confirm(`Clear reminder for ${label}?\n\nThis will hide the row from Pending Reminders until you un-clear it. The outstanding balance itself stays untouched.`)) return;
    }
    setBusyId(id + ":" + action);
    try {
      await api.post(`/dashboard/pending-reminders/${id}/action`, { action, ...extra });
      setItems((prev) => prev.filter((x) => x.id !== id));
    } finally {
      setBusyId("");
    }
  };

  const openWhatsApp = (row) => {
    if (!row.mobile) { toast.error("No mobile on record"); return; }
    setWaRow(row);
  };

  const handleClose = (v) => {
    if (!v && autoTriggered && user?.id) {
      localStorage.setItem(STORAGE_KEY(user.id), new Date().toISOString());
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0"
        data-testid="pending-reminders-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Pending Payments — {minDays}+ Days Overdue
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {items.length > 0
              ? `${items.length} customer${items.length > 1 ? "s" : ""} with outstanding balance. Please follow up.`
              : loading
              ? "Loading pending reminders…"
              : "No overdue pending customers. You are all caught up."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {items.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <div className="text-sm font-semibold text-slate-600">All clear!</div>
              <div className="text-xs">No customers have unpaid balances older than 15 days.</div>
            </div>
          ) : (
            <table className="w-full text-sm crm-table" data-testid="pending-reminders-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-2 text-left font-semibold">Application ID</th>
                  <th className="px-3 py-2 text-left font-semibold">Service</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance</th>
                  <th className="px-3 py-2 text-right font-semibold">Days Overdue</th>
                  <th className="px-3 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} data-testid={`pending-row-${r.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-600">{formatBill(r.application_id || r.doc_no)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.service_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num font-bold text-red-600">{inr(r.balance)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200">
                        {r.days_overdue}d
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 justify-end">
                        <Button
                          size="sm"
                          onClick={() => openWhatsApp(r)}
                          className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                          data-testid={`whatsapp-btn-${r.id}`}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setFollowUpRow(r)}
                          className="h-7 px-2 bg-[color:var(--brand-navy)] hover:bg-slate-800 text-white text-[11px]"
                          data-testid={`follow-up-btn-${r.id}`}
                          title="Add to Follow-Up log"
                        >
                          <CalendarClock className="h-3 w-3 mr-1" /> Follow-Up
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => doAction(r.id, "contacted")}
                          disabled={busyId === r.id + ":contacted"}
                          className="h-7 px-2 bg-slate-700 hover:bg-slate-800 text-white text-[11px]"
                          data-testid={`contacted-btn-${r.id}`}
                        >
                          <PhoneCall className="h-3 w-3 mr-1" /> Mark Contacted
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => doAction(r.id, "snooze", { days: 15 })}
                          disabled={busyId === r.id + ":snooze"}
                          className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white text-[11px]"
                          data-testid={`snooze-btn-${r.id}`}
                        >
                          <Clock className="h-3 w-3 mr-1" /> Snooze 15d
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => doAction(r.id, "clear")}
                          disabled={busyId === r.id + ":clear"}
                          variant="outline"
                          className="h-7 px-2 text-[11px] border-slate-300"
                          data-testid={`clear-reminder-btn-${r.id}`}
                          title="Clear — hide until you un-clear"
                        >
                          <X className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Rows are cleared automatically once fully paid. Use <span className="font-semibold">Clear</span> to hide until un-cleared. Snooze 15d hides temporarily.
          </div>
          <Button
            onClick={() => handleClose(false)}
            className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold"
            data-testid="pending-reminders-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Application ${formatBill(waRow.application_id || waRow.doc_no)}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Reminder"
        />
      )}
    </Dialog>
  );
}

/**
 * Plain helper (NOT a React hook) to decide whether auto-popup should show.
 * Reads localStorage entry per user. Returns { eligible, shouldShow }.
 */
export function shouldAutoShowReminders(userId, role) {
  const eligible = ["admin", "branch", "manager"].includes(role);
  if (!eligible || !userId) return { eligible: false, shouldShow: false };
  const last = localStorage.getItem(STORAGE_KEY(userId));
  const days = daysSinceISO(last);
  return { eligible: true, shouldShow: days >= CYCLE_DAYS };
}
